/* KABO hero dust — the "snap" stage.
 *
 * Original canvas particle system, no libraries, no assets:
 *   - samples the wordmark from an offscreen canvas into ~2k particle targets
 *   - particles fly in from scatter with critically-damped springs (assemble)
 *   - scroll progress (--kb-p, set by the scrub engine on the hero section)
 *     past the midpoint blows them away up-and-right with per-particle drag —
 *     the disintegration — and reassembles in reverse on scroll-up
 *   - a sparse layer of free embers drifts continuously for ambient warmth
 *
 * Budget: DPR capped at 2, one rAF, IntersectionObserver-gated (parks offscreen),
 * pauses on hidden tabs. Reduced motion renders the assembled word once, static.
 *
 * Perf (31 Oct 2026): where the browser supports it, the per-frame loop runs in a
 * Web Worker on an OffscreenCanvas (transferControlToOffscreen), so the 60fps
 * redraw no longer competes with the main thread (TBT / INP). The worker runs
 * the SAME frame() maths and draw calls; particle data is still generated here,
 * scroll progress (--kb-p) and visibility are forwarded by message. Browsers
 * without OffscreenCanvas / worker rAF fall back to the original main-thread loop.
 */
(function () {
  'use strict';

  var host = document.querySelector('[data-kb-dust]');
  if (!host) return;

  var word = host.getAttribute('data-kb-dust') || '';   // empty = embers only
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var cv = document.createElement('canvas');
  cv.setAttribute('aria-hidden', 'true');
  host.appendChild(cv);
  var ctx = null;   // acquired in mainThread(): a canvas with a 2D context can no longer be transferred to a worker
  var DPR = Math.min(window.devicePixelRatio || 1, 2);

  var W = 0, H = 0, parts = [], embers = [];
  var GOLD = ['#E8B45A', '#C9A227', '#EFA06B', '#D9A97C'];

  function palette() {
    // ~1 in 9 particles carries the brand red so the mass reads warm, not yellow
    return Math.random() < 0.11 ? '#B91C2E' : GOLD[(Math.random() * GOLD.length) | 0];
  }

  function build() {
    W = host.clientWidth; H = host.clientHeight;
    if (!W || !H) return;
    cv.width = W * DPR; cv.height = H * DPR;
    cv.style.width = W + 'px'; cv.style.height = H + 'px';
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);

    if (!word) { parts = []; makeEmbers(64); return; }
    var oc = document.createElement('canvas');
    oc.width = W; oc.height = H;
    var o = oc.getContext('2d');
    var fs = Math.min((W * 0.92) / (word.length * 0.60), H * 0.62);
    o.font = '800 ' + fs + 'px Archivo, Assistant, system-ui, sans-serif';
    o.textAlign = 'center'; o.textBaseline = 'middle';
    o.fillStyle = '#000';
    o.fillText(word, W / 2, H / 2 - H * 0.04);
    var img = o.getImageData(0, 0, W, H).data;

    var gap = Math.max(3, Math.round(W / 200));
    var targets = [];
    for (var y = 0; y < H; y += gap) {
      for (var x = 0; x < W; x += gap) {
        if (img[(y * W + x) * 4 + 3] > 128) targets.push([x, y]);
      }
    }
    while (targets.length > 2400) targets.splice((Math.random() * targets.length) | 0, 1);

    parts = targets.map(function (t) {
      var edge = Math.random();
      return {
        x: edge < .5 ? Math.random() * W : (Math.random() < .5 ? -30 : W + 30),
        y: edge < .5 ? (Math.random() < .5 ? -30 : H + 30) : Math.random() * H,
        vx: 0, vy: 0, tx: t[0], ty: t[1],
        r: Math.random() * 1.5 + .9,
        c: palette(),
        w: Math.random() * 1.2 + .55,           // wind susceptibility (per-particle drag)
        ph: Math.random() * 6.283
      };
    });

    makeEmbers(42);
  }

  function makeEmbers(count) {
    embers = [];
    for (var i = 0; i < count; i++) {
      embers.push({
        x: Math.random() * W, y: Math.random() * H,
        r: Math.random() * 1.8 + .6,
        s: Math.random() * .35 + .12,            // rise speed
        c: palette(), a: Math.random() * .4 + .15,
        ph: Math.random() * 6.283
      });
    }
  }

  function drawStatic() {
    ctx.clearRect(0, 0, W, H);
    if (!parts.length) return;
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i];
      ctx.globalAlpha = .95;
      ctx.fillStyle = p.c;
      ctx.beginPath(); ctx.arc(p.tx, p.ty, p.r, 0, 6.283); ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  var scrubEl = host.closest('[data-kb-scrub]');

  if (reduce || !tryWorker()) mainThread();

  /* Perf (31 Oct 2026): park the hero's infinite CSS loops while the hero is
     completely off-screen. Two of them (orbit colour pulse, label letter-spacing)
     cannot run on the compositor, so they forced a style recalc on the main thread
     every frame for the whole visit, even after the visitor had scrolled away.
     .kb-offscreen pauses them (CSS in the section); they resume from the same
     frame when the hero scrolls back in. One-shot entrance animations are never
     cut short: the pause waits for any still-running finite animation to end. */
  (function parkWhenAway(sec) {
    if (!sec || !window.IntersectionObserver || !sec.getAnimations) return;
    var away = false, waiting = false;
    function apply() {
      if (!away) { sec.classList.remove('kb-offscreen'); return; }
      var pending = sec.getAnimations({ subtree: true }).filter(function (a) {
        var tm = a.effect && a.effect.getTiming ? a.effect.getTiming() : null;
        return tm && tm.iterations !== Infinity && a.playState === 'running';
      });
      if (pending.length) {
        if (waiting) return;
        waiting = true;
        Promise.all(pending.map(function (a) { return a.finished.catch(function () {}); }))
          .then(function () { waiting = false; apply(); });
        return;
      }
      sec.classList.add('kb-offscreen');
    }
    new IntersectionObserver(function (entries) {
      away = !entries[entries.length - 1].isIntersecting;
      apply();
    }, { threshold: 0 }).observe(sec);
  })(scrubEl);

  function mainThread() {
  ctx = cv.getContext('2d');
  build();
  if (reduce) { drawStatic(); return; }

  var visible = false, raf = null, t0 = performance.now();

  function frame(now) {
    raf = null;
    var t = (now - t0) / 1000;
    var kp = .5;
    if (scrubEl) {
      var v = parseFloat(scrubEl.style.getPropertyValue('--kb-p'));
      if (!isNaN(v)) kp = v;
    }
    // 0 while hero centred; ramps to 1 as it leaves the top — the snap
    var snap = Math.min(1, Math.max(0, (kp - .55) * 2.8));

    ctx.clearRect(0, 0, W, H);

    for (var i = 0; i < embers.length; i++) {
      var e = embers[i];
      e.y -= e.s; e.x += Math.sin(t * .7 + e.ph) * .18;
      if (e.y < -6) { e.y = H + 6; e.x = Math.random() * W; }
      ctx.globalAlpha = e.a * (0.75 + 0.25 * Math.sin(t * 2 + e.ph));
      ctx.fillStyle = e.c;
      ctx.beginPath(); ctx.arc(e.x, e.y, e.r, 0, 6.283); ctx.fill();
    }

    for (var j = 0; j < parts.length; j++) {
      var p = parts[j];
      // wind target during the snap: up and away, per-particle
      var gx = p.tx + snap * (60 + 160 * p.w);
      var gy = p.ty - snap * (40 + 200 * p.w);
      p.vx = (p.vx + (gx - p.x) * .045) * .84;
      p.vy = (p.vy + (gy - p.y) * .045) * .84;
      p.x += p.vx; p.y += p.vy;

      var shimmer = .88 + .12 * Math.sin(t * 1.6 + p.ph);
      ctx.globalAlpha = Math.max(0, (1 - snap * 1.15)) * shimmer;
      ctx.fillStyle = p.c;
      ctx.beginPath();
      ctx.arc(p.x + Math.sin(t + p.ph) * .5, p.y + Math.cos(t * .8 + p.ph) * .5, p.r, 0, 6.283);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    if (visible && !document.hidden) raf = requestAnimationFrame(frame);
  }

  function start() { if (!raf) { t0 = performance.now() - 1; raf = requestAnimationFrame(frame); } }

  var io = new IntersectionObserver(function (entries) {
    visible = entries[0].isIntersecting;
    if (visible) start();
  }, { threshold: 0.05 });
  io.observe(host);

  document.addEventListener('visibilitychange', function () { if (!document.hidden && visible) start(); });

  var rt = null;
  window.addEventListener('resize', function () {
    clearTimeout(rt);
    rt = setTimeout(function () { build(); if (visible) start(); }, 180);
  }, { passive: true });
  } // end mainThread

  /* ---- off-main-thread renderer -------------------------------------------
     Same frame() as mainThread(), executed in a worker against an OffscreenCanvas. */
  function tryWorker() {
    if (typeof cv.transferControlToOffscreen !== 'function' || typeof Worker === 'undefined' ||
        typeof OffscreenCanvas === 'undefined' || !window.Blob || !window.URL || !URL.createObjectURL) return false;

    var code = [
      'var cv=null,ctx=null,W=0,H=0,DPR=1,parts=[],embers=[],kp=.5,running=false,raf=null,t0=performance.now();',
      'function frame(now){',
      '  raf=null;',
      '  var t=(now-t0)/1000;',
      '  var snap=Math.min(1,Math.max(0,(kp-.55)*2.8));',
      '  ctx.clearRect(0,0,W,H);',
      '  for(var i=0;i<embers.length;i++){',
      '    var e=embers[i];',
      '    e.y-=e.s; e.x+=Math.sin(t*.7+e.ph)*.18;',
      '    if(e.y<-6){e.y=H+6;e.x=Math.random()*W;}',
      '    ctx.globalAlpha=e.a*(0.75+0.25*Math.sin(t*2+e.ph));',
      '    ctx.fillStyle=e.c;',
      '    ctx.beginPath();ctx.arc(e.x,e.y,e.r,0,6.283);ctx.fill();',
      '  }',
      '  for(var j=0;j<parts.length;j++){',
      '    var p=parts[j];',
      '    var gx=p.tx+snap*(60+160*p.w);',
      '    var gy=p.ty-snap*(40+200*p.w);',
      '    p.vx=(p.vx+(gx-p.x)*.045)*.84;',
      '    p.vy=(p.vy+(gy-p.y)*.045)*.84;',
      '    p.x+=p.vx;p.y+=p.vy;',
      '    var shimmer=.88+.12*Math.sin(t*1.6+p.ph);',
      '    ctx.globalAlpha=Math.max(0,(1-snap*1.15))*shimmer;',
      '    ctx.fillStyle=p.c;',
      '    ctx.beginPath();',
      '    ctx.arc(p.x+Math.sin(t+p.ph)*.5,p.y+Math.cos(t*.8+p.ph)*.5,p.r,0,6.283);',
      '    ctx.fill();',
      '  }',
      '  ctx.globalAlpha=1;',
      '  if(running)raf=requestAnimationFrame(frame);',
      '}',
      'function start(){if(!raf&&ctx){t0=performance.now()-1;raf=requestAnimationFrame(frame);}}',
      'self.onmessage=function(m){var d=m.data;',
      '  if(d.type==="canvas"){cv=d.canvas;ctx=cv.getContext("2d");}',
      '  else if(d.type==="build"){W=d.W;H=d.H;DPR=d.DPR;parts=d.parts;embers=d.embers;',
      '    if(W&&H){cv.width=W*DPR;cv.height=H*DPR;ctx.setTransform(DPR,0,0,DPR,0,0);}',
      '    if(running)start();}',
      '  else if(d.type==="kp"){kp=d.kp;}',
      '  else if(d.type==="run"){running=d.run;if(running)start();}',
      '};',
      'self.postMessage({ok:typeof requestAnimationFrame==="function"});'
    ].join('\n');

    var url, wk;
    try {
      url = URL.createObjectURL(new Blob([code], { type: 'text/javascript' }));
      wk = new Worker(url);
    } catch (e) { if (url) URL.revokeObjectURL(url); return false; }

    var settled = false;
    function fallback() {
      // the worker cannot run frames here: use the original main-thread path
      try { wk.terminate(); } catch (e) {}
      try { URL.revokeObjectURL(url); } catch (e) {}
      mainThread();
    }
    var guard = setTimeout(function () { if (!settled) { settled = true; fallback(); } }, 4000);

    wk.onerror = function () { if (!settled) { settled = true; clearTimeout(guard); fallback(); } };
    wk.onmessage = function (m) {
      if (settled) return;
      settled = true; clearTimeout(guard);
      try { URL.revokeObjectURL(url); } catch (e) {}
      if (!m.data || !m.data.ok) { fallback(); return; }
      var off;
      try { off = cv.transferControlToOffscreen(); } catch (e) { fallback(); return; }
      wk.postMessage({ type: 'canvas', canvas: off }, [off]);
      runInWorker(wk);
    };
    return true;
  }

  function runInWorker(wk) {
    // particle data is generated here exactly as build() does (it needs the DOM
    // and the page's webfonts for the wordmark sample); the worker owns the canvas
    // bitmap, so only its CSS size is set on this side.
    function rebuild() {
      W = host.clientWidth; H = host.clientHeight;
      if (!W || !H) { wk.postMessage({ type: 'build', W: 0, H: 0, DPR: DPR, parts: [], embers: [] }); return; }
      cv.style.width = W + 'px'; cv.style.height = H + 'px';
      if (!word) { parts = []; makeEmbers(64); }
      else {
        var oc = document.createElement('canvas');
        oc.width = W; oc.height = H;
        var o = oc.getContext('2d');
        var fs = Math.min((W * 0.92) / (word.length * 0.60), H * 0.62);
        o.font = '800 ' + fs + 'px Archivo, Assistant, system-ui, sans-serif';
        o.textAlign = 'center'; o.textBaseline = 'middle';
        o.fillStyle = '#000';
        o.fillText(word, W / 2, H / 2 - H * 0.04);
        var img = o.getImageData(0, 0, W, H).data;
        var gap = Math.max(3, Math.round(W / 200));
        var targets = [];
        for (var y = 0; y < H; y += gap) {
          for (var x = 0; x < W; x += gap) {
            if (img[(y * W + x) * 4 + 3] > 128) targets.push([x, y]);
          }
        }
        while (targets.length > 2400) targets.splice((Math.random() * targets.length) | 0, 1);
        parts = targets.map(function (t) {
          var edge = Math.random();
          return {
            x: edge < .5 ? Math.random() * W : (Math.random() < .5 ? -30 : W + 30),
            y: edge < .5 ? (Math.random() < .5 ? -30 : H + 30) : Math.random() * H,
            vx: 0, vy: 0, tx: t[0], ty: t[1],
            r: Math.random() * 1.5 + .9,
            c: palette(),
            w: Math.random() * 1.2 + .55,
            ph: Math.random() * 6.283
          };
        });
        makeEmbers(42);
      }
      wk.postMessage({ type: 'build', W: W, H: H, DPR: DPR, parts: parts, embers: embers });
    }
    rebuild();

    // scroll progress: the scrub engine writes --kb-p on the section's style
    function sendKp() {
      var v = scrubEl ? parseFloat(scrubEl.style.getPropertyValue('--kb-p')) : NaN;
      wk.postMessage({ type: 'kp', kp: isNaN(v) ? .5 : v });
    }
    sendKp();
    if (scrubEl && window.MutationObserver) {
      new MutationObserver(sendKp).observe(scrubEl, { attributes: true, attributeFilter: ['style'] });
    }

    var vis = false;
    function sendRun() { wk.postMessage({ type: 'run', run: vis && !document.hidden }); }
    new IntersectionObserver(function (entries) {
      vis = entries[0].isIntersecting;
      sendRun();
    }, { threshold: 0.05 }).observe(host);
    document.addEventListener('visibilitychange', sendRun);

    var rt2 = null;
    window.addEventListener('resize', function () {
      clearTimeout(rt2);
      rt2 = setTimeout(rebuild, 180);
    }, { passive: true });
  }
})();
